import { Container, Graphics, LINE_JOIN, Text } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"
import { RIM_LIGHT, TIMBER_EDGE } from "./build_board"
import { HUD_PANEL_FILL, HUD_PANEL_RIM, HUD_PANEL_TEXT } from "./ui_tokens"

// The speech bubble that floats above the ember roster strip when a camp member says something the
// player can't see them say (they're outside the viewport). Its tail points down at the speaker's
// roster card, so the line is tied to a face and a name rather than floating free.
//
// ONE bubble exists and is re-aimed as the lane's occupant changes — see EmberRosterWidget, which
// owns the queue. Keeping it single is the whole clutter strategy: a bubble is far wider than the
// ~56px card it belongs to, so two of them side by side would overlap and neither would be readable.
//
// It fades in and out rather than popping, and a HANDOVER between two speakers fades the old line
// out before the new one fades in — the tail moves to a different card, so cross-dissolving in place
// would read as the bubble teleporting. The fade is driven by the roster's existing per-frame tick
// rather than the Fader utility, which spawns a setInterval per fade; this runs on every frame the
// roster already animates, and timer churn is worth avoiding on the low-end hardware this targets.

// THE LOOK (restyled 2026-10-10): the in-world chat bubble's own cut-timber panel
// (sprites/avatar_hud.ts spokeImpl) -- HUD_PANEL plate, chamfered corners, an inked bronze rim, a
// dropped shadow and a lit top edge, body and tail ONE path -- so a line reads as the same object
// whether it is said beside you or relayed from off screen. It used to be a rounded rect ringed and
// haloed in the speaker's ROLE colour, with the tail drawn as a second shape, centred text with a
// black outline, and the name centred over it like a title card. The role colour now lives in one
// place: the speaker's NAME, set as a small capitals tag above the line. The card the tail lands on
// already pulses in that colour while its member speaks (EmberRosterWidget.drawSpeakingRing), so
// the tie between bubble and card does not need the rim as well.
const PANEL_FILL = HUD_PANEL_FILL
const PANEL_ALPHA = 0.97
const TEXT_COLOR = HUD_PANEL_TEXT

const PAD_X = 10
const PAD_Y = 7
const CHAMFER = 6
const TAIL_H = 8
const TAIL_HALF_W = 6
const LINE_GAP = 2
// The rim, as on the world bubble: a wide dark stroke with a thin bronze one down its middle.
// (In roster units, which the strip draws at 0.8 -- so these land at the world bubble's 3.5 / 1.25.)
const EDGE_WIDTH = 4.4
const EDGE_ALPHA = 0.95
const RIM_WIDTH = 1.5
const MITER_LIMIT = 3 // bevels the tail's tip rather than spiking it
const SHADOW_Y = 3
const SHADOW_ALPHA = 0.38
const TOP_LIGHT_ALPHA = 0.2
const NAME_SPACING = 1.2

// EVERY size here is in the roster widget's local units, which the strip then draws at
// WIDGET_SCALE (0.8) — so a font set to 15 lands at ~12px on screen. 12px is deliberate: it is what
// the in-world chat bubble uses (the shared "speech" role), so a line reads the same whether
// you happen to be standing next to the speaker or reading it off the HUD.
const ROSTER_SCALE = 0.8
const SCREEN_MSG_PX = TEXT_ROLES.speech.fontSize
const SCREEN_NAME_PX = TEXT_ROLES.compactCaption.fontSize
const MSG_SIZE = Math.round(SCREEN_MSG_PX / ROSTER_SCALE)
const NAME_SIZE = Math.round(SCREEN_NAME_PX / ROSTER_SCALE)

// Wide enough for a sentence over two lines at that size, narrow enough to sit over the strip.
const MAX_W = 250
// Hard cap so a long line can't grow the bubble without bound. The longest thing anyone currently
// says (the Lord's camp-news lines) is ~60 characters.
const MAX_CHARS = 72
// The name line doesn't wrap, so an over-long name would render wider than the body it sits in and
// spill out both sides of the bubble. Cap it.
const MAX_NAME_CHARS = 18

// Rough line box for a font size — Verdana's ascent+descent plus the 1px stroke TextRenderer adds.
const lineHeight = (fontSize: number) => Math.ceil(fontSize * 1.35)

// The tallest the bubble can get: a name line plus three wrapped message lines. The roster reserves
// this much room above the strip so the follow-cam PiP sitting above it never has to move, and so a
// long line can't clip. Budgeting three lines (MAX_CHARS usually wraps to two) keeps that true even
// for an awkward wrap.
export const MAX_BUBBLE_H =
    PAD_Y * 2 + lineHeight(NAME_SIZE) + LINE_GAP + 3 * lineHeight(MSG_SIZE) + TAIL_H

// Half the dark edge stroke paints outside the body's rect. The clamp has to allow for it or the
// rim is what runs off-screen.
const GLOW_PAD = Math.ceil(EDGE_WIDTH / 2) + 1

export interface BubbleBox {
    left: number
    top: number
    width: number
    height: number
}

// Where the bubble body sits for a given tail and content size — pure, so the one thing that must
// never go wrong (running off the right of the screen) can be tested without a renderer.
//
// The body is centred over the tail, then slid LEFT if it would overhang `clampRight` (the roster
// strip's right edge, which is itself hard against the screen edge). It is never slid right: there
// is open screen to the left of the strip, so the bubble grows that way. The tail stays on its own
// card either way — it is what identifies the speaker.
export const layoutBubble = (contentW: number, contentH: number, tailX: number, tailY: number, clampRight: number): BubbleBox => {
    const width = Math.min(MAX_W, contentW + PAD_X * 2)
    const height = contentH + PAD_Y * 2
    const rightLimit = clampRight - GLOW_PAD
    let left = tailX - width / 2
    if (left + width > rightLimit) {
        left = rightLimit - width
    }
    return { left, top: tailY - TAIL_H - height, width, height }
}

const FADE_IN_MS = 160
const FADE_OUT_MS = 220

// What the lane wants shown this frame. `key` identifies the utterance, so the bubble can tell a
// re-aim (same line, card moved) from a handover to a different line.
export interface BubbleTarget {
    key: string
    name: string
    text: string
    tailX: number
    tailY: number
    clampRight: number
    color: number
}

export class RosterSpeechBubble extends Container {
    private bg: Graphics
    private nameText: Text
    private messageText: Text
    // The utterance currently drawn (null while nothing is), and where the fade is up to.
    private shownKey: string | null = null
    private fade = 0

    constructor() {
        super()
        this.bg = new Graphics()
        // The speaker's name: a small letterspaced capitals tag, the one thing in the role's colour.
        this.nameText = new TextRenderer().role("compactCaption").fontSize(NAME_SIZE).bold().strokeThickness(0)
            .render("") as Text
        this.nameText.style.letterSpacing = NAME_SPACING
        // Left-aligned, like the world bubble's lines: a two-line wrap reads better from one edge
        // than centred on a ragged middle. No outline -- it sits on a solid plate.
        this.messageText = new TextRenderer().role("speech")
            .fontSize(MSG_SIZE)
            .strokeThickness(0)
            .wordWrap(MAX_W - PAD_X * 2)
            .render("") as Text
        this.messageText.tint = TEXT_COLOR
        this.addChild(this.bg, this.nameText, this.messageText)
        this.visible = false
    }

    // Drive one frame of the bubble toward `target` (null = nothing to say). Handles the fade and
    // the swap, so the caller can simply hand over whatever the lane picked each frame.
    update = (dtMs: number, target: BubbleTarget | null) => {
        const wantsSwap = target !== null && this.shownKey !== null && target.key !== this.shownKey
        // Fade OUT when there's nothing to show, or when a different speaker is taking the lane —
        // the new line only appears once the old one has cleared.
        if (target === null || wantsSwap) {
            this.fade = Math.max(0, this.fade - dtMs / FADE_OUT_MS)
            if (this.fade <= 0) {
                this.shownKey = null
                this.visible = false
            }
            this.alpha = this.fade
            return
        }

        if (this.shownKey !== target.key) {
            this.draw(target)
            this.shownKey = target.key
            this.visible = true
        }
        this.fade = Math.min(1, this.fade + dtMs / FADE_IN_MS)
        this.alpha = this.fade
    }

    // Lay the bubble out around its tail. `tailX`/`tailY` are the tail TIP in the parent's
    // coordinate space (the top-centre of the speaking card); the body is drawn above it, and
    // `clampRight` keeps it from spilling past the right edge of the roster strip.
    private draw = (target: BubbleTarget) => {
        const { name, text, tailX, tailY, clampRight, color } = target
        const trimmed = text.length > MAX_CHARS ? text.slice(0, MAX_CHARS - 1) + "…" : text
        if (this.messageText.text !== trimmed) {
            this.messageText.text = trimmed
        }
        const shortName = (name.length > MAX_NAME_CHARS ? name.slice(0, MAX_NAME_CHARS - 1) + "…" : name).toUpperCase()
        if (this.nameText.text !== shortName) {
            this.nameText.text = shortName
        }
        this.nameText.tint = color

        // letterSpacing adds a trailing advance after the last capital; it is not ink.
        const nameW = Math.max(0, this.nameText.width - NAME_SPACING)
        const contentW = Math.max(nameW, this.messageText.width)
        const contentH = this.nameText.height + LINE_GAP + this.messageText.height
        const box = layoutBubble(contentW, contentH, tailX, tailY, clampRight)
        const { left: bodyLeft, top: bodyTop, width: bodyW, height: bodyH } = box

        this.drawBubble(bodyLeft, bodyTop, bodyW, bodyH, tailX, tailY)

        const textX = Math.round(bodyLeft + PAD_X)
        this.nameText.position.set(textX, Math.round(bodyTop + PAD_Y))
        this.messageText.position.set(textX, Math.round(bodyTop + PAD_Y + this.nameText.height + LINE_GAP))
    }

    // Body + tail as ONE path, filled and stroked once, so the rim runs unbroken round the tail
    // (drawing the tail as a second shape left a seam and a fill mismatch on the world bubble).
    private drawBubble = (left: number, top: number, w: number, h: number, tailX: number, tailY: number) => {
        const g = this.bg
        const c = CHAMFER
        const right = left + w
        const bottom = top + h
        // The tail's base slides along the bottom edge to sit under the speaker's card, clear of the
        // chamfers; its tip stays on the card, leaning when the body had to slide left to fit.
        const baseX = Math.max(left + c + TAIL_HALF_W + 1, Math.min(right - c - TAIL_HALF_W - 1, tailX))
        const trace = (oy: number) => {
            g.moveTo(left + c, top + oy)
            g.lineTo(right - c, top + oy)
            g.lineTo(right, top + c + oy)
            g.lineTo(right, bottom - c + oy)
            g.lineTo(right - c, bottom + oy)
            g.lineTo(baseX + TAIL_HALF_W, bottom + oy)
            g.lineTo(tailX, tailY + oy)
            g.lineTo(baseX - TAIL_HALF_W, bottom + oy)
            g.lineTo(left + c, bottom + oy)
            g.lineTo(left, bottom - c + oy)
            g.lineTo(left, top + c + oy)
            g.closePath()
        }

        g.clear()
        // Dropped silhouette, straight down: a diagonal offset ghosts a second tail beside the real one.
        g.lineStyle(0)
        g.beginFill(0x000000, SHADOW_ALPHA)
        trace(SHADOW_Y)
        g.endFill()

        g.lineStyle({ width: EDGE_WIDTH, color: TIMBER_EDGE, alpha: EDGE_ALPHA, join: LINE_JOIN.MITER, miterLimit: MITER_LIMIT })
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        trace(0)
        g.endFill()
        g.lineStyle({ width: RIM_WIDTH, color: HUD_PANEL_RIM, alpha: 1, join: LINE_JOIN.MITER, miterLimit: MITER_LIMIT })
        trace(0)

        // One lit edge along the top, so the panel has a direction the light falls from.
        g.lineStyle(1, RIM_LIGHT, TOP_LIGHT_ALPHA)
        g.moveTo(left + c + 1, top + 2.5).lineTo(right - c - 1, top + 2.5)
        g.lineStyle(0)
    }
}
